import {
	IconProps,
	ErrorFilled,
	SuccessFilled,
} from '@velocity-exchange/icons';
import { ReactNode, ComponentType } from 'react';
import XButton from 'src/components/Buttons/XButton';
import InfoFilled from 'src/components/Icons/InfoFilled';
import WarningFilled from 'src/components/Icons/WarningFilled';
import { cj, cm } from 'src/utils/classNames';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';

export type InlineAlertVariant = 'info' | 'warning' | 'error' | 'success';

interface BaseProps extends WithChildren, WithClassName {
	variant: InlineAlertVariant;
	title: ReactNode;
	customIcon?: ComponentType<IconProps>;
}

type DismissibleProps =
	| {
			dismissible: true;
			onDismiss: () => void;
	  }
	| {
			dismissible?: false;
			onDismiss?: never;
	  };

type Props = BaseProps & DismissibleProps;

export default function InlineAlert({
	variant,
	title,
	children,
	className,
	dismissible,
	onDismiss,
	customIcon: CustomIcon,
}: Props) {
	const variantClassName = {
		info: 'bg-stroke-divider [&_[data-icon]_path]:fill-text-default',
		warning:
			'bg-warn-orange-secondary-bg [&_[data-icon]_path]:fill-star-yellow',
		error:
			'bg-negative-red-secondary-bg [&_[data-icon]_path]:fill-negative-red',
		success:
			'bg-positive-green-secondary-bg [&_[data-icon]_path]:fill-positive-green',
	}[variant];

	const VariantIcon = {
		info: InfoFilled,
		warning: WarningFilled,
		error: ErrorFilled,
		success: SuccessFilled,
	}[variant];

	return (
		<div
			className={cm(
				'flex items-start gap-x-sm rounded-sm p-md text-text-default',
				variantClassName,
				className
			)}
		>
			<span className="flex" data-icon>
				{CustomIcon ? <CustomIcon size={16} /> : <VariantIcon size={16} />}
			</span>
			<div className="flex flex-col items-start gap-y-sm">
				<span
					className={cj(
						'typo-label-xs',
						variant === 'error' && 'text-text-negative-red-button'
					)}
				>
					{title}
				</span>
				{children &&
					(Array.isArray(children)
						? children.filter(Boolean).length > 0
						: true) && (
						<span className="flex min-w-0 flex-col gap-y-xs typo-body-2xs">
							{children}
						</span>
					)}
			</div>
			{dismissible && (
				<XButton size="sm" className="ml-auto" onClick={onDismiss} />
			)}
		</div>
	);
}
